import { stringify } from 'qs';
import { getLoadManagerAxiosInstance } from '../../../../../../common/services/axios-config';
import ILoadHistoryResponse, {
    ILoadHistoryData,
} from '../../../interfaces/ILoadHistoryResponse';
import i18n from '../../../../../../lib/i18n';

const createEmptyResponse = (): ILoadHistoryResponse =>
    ({
        currentPage: 0,
        data: [{ events: [] }] as ILoadHistoryData[],
        totalItems: 0,
        totalPages: 1,
    }) as ILoadHistoryResponse;

const extractEventsFromResponse = (
    responsePayload: ILoadHistoryResponse | undefined,
): ILoadHistoryData['events'] => {
    const paginatedData = Array.isArray(responsePayload?.data)
        ? responsePayload?.data
        : [];
    return paginatedData?.[0]?.events ?? [];
};

const mergeResponseMetadata = (
    target: ILoadHistoryResponse,
    source: ILoadHistoryResponse | undefined,
): void => {
    if (!source) return;

    if (typeof source.currentPage === 'number') {
        target.currentPage = source.currentPage;
    }

    if (typeof source.totalPages === 'number') {
        target.totalPages = source.totalPages > 0 ? source.totalPages : target.totalPages;
    }

    if (typeof source.totalItems === 'number') {
        target.totalItems = source.totalItems;
    }

    if (typeof source.pageSize === 'number') {
        target.pageSize = source.pageSize;
    }

    if (source.loadId) {
        target.loadId = source.loadId;
    }

    if (source.country) {
        target.country = source.country;
    }
};

const fetchHistoryPage = async (
    api: any,
    request: { LoadId: string; _page: number; _size: number },
): Promise<ILoadHistoryResponse | undefined> => {
    const response = await api
        .get('v1/loads/history:paginated', {
            params: request,
            paramsSerializer(params: any) {
                return stringify(params, { allowDots: true });
            },
        })
        .catch(() => {
            throw new Error(i18n.t('LoadHistory.ErrorGettingHistory'));
        });

    return response?.data as ILoadHistoryResponse | undefined;
};

const getAllLoadHistoryPaginated = async (
    loadId: string,
): Promise<ILoadHistoryResponse> => {
    const request = {
        LoadId: loadId,
        _page: 1,
        _size: 50,
    };

    const api = await getLoadManagerAxiosInstance();
    const responseData = createEmptyResponse();

    do {
        const responsePayload = await fetchHistoryPage(api, request);
        const events = extractEventsFromResponse(responsePayload);

        if (events.length > 0) {
            responseData.data[0].events.push(...events);
        }

        mergeResponseMetadata(responseData, responsePayload);

        if (events.length === 0) {
            break;
        }

        request._page++;
    } while (request._page <= responseData.totalPages);

    return responseData;
};

const LoadHistoryService = {
    getAllLoadHistoryPaginated,
};

export default LoadHistoryService;
